import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useBinancePairsTicker } from "../../hooks/useBinancePairsTicker";
import { usePersistentPairSelection } from "../../hooks/usePersistentPairSelection";
import { UpDownChartBoard } from "../../features/futures/modes/up-down/components";
import { useWallet } from "../../contexts/walletContext";
import Axios from "../../Axios";
import consts from "../../constant";
import Loader from "../../components/loader";
import { HapticBalance } from "../../components/HapticBalance";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { Menu } from "lucide-react";
import { playMultiplierSound, playSound } from "../../components/Soundmaker";
import { EZ5050TradingLayout } from "../../components/trading/EZ5050TradingLayout";

// â”€â”€â”€ Constants â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
const LAYOUT_STORAGE_KEY = "layout.mode";
const TIMEFRAME_OPTIONS = new Set(["Tick", "5s", "1m", "5m", "15m", "30m", "1h"]);
const CHART_TYPE_OPTIONS = new Set(["candle", "area"]);
const MOBILE_LAYOUT_MEDIA_QUERY = "(max-width: 979px)";

// Structural chrome heights (px)
const HEADER_H = 58; // sticky .app-top-header
const GRID_GAP = 8; // gap between rows / cols

export const LAYOUT_OPTIONS = [
  { id: "single", label: "Single", panels: 1, cols: 1 },
  { id: "dual", label: "Dual", panels: 2, cols: 1 },
  // 2+1: top-two side-by-side, bottom-one full-width
  { id: "triple", label: "Triple", panels: 3, cols: 2, variant: "2+1" },
  { id: "quad", label: "Quad", panels: 4, cols: 2 },
];

// â”€â”€â”€ SVG layout icons â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
const ICON = {
  single: (
    <svg width="20" height="14" viewBox="0 0 20 14" fill="currentColor">
      <rect x="1" y="1" width="18" height="12" rx="2" />
    </svg>
  ),
  dual: (
    <svg width="20" height="14" viewBox="0 0 20 14" fill="currentColor">
      <rect x="1" y="1" width="8" height="12" rx="1.5" />
      <rect x="11" y="1" width="8" height="12" rx="1.5" />
    </svg>
  ),
  triple: (
    // 2+1: two panels on top, one full-width on bottom
    <svg width="20" height="14" viewBox="0 0 20 14" fill="currentColor">
      <rect x="1" y="1" width="8" height="5" rx="1.5" />
      <rect x="11" y="1" width="8" height="5" rx="1.5" />
      <rect x="1" y="8" width="18" height="5" rx="1.5" />
    </svg>
  ),
  quad: (
    <svg width="20" height="14" viewBox="0 0 20 14" fill="currentColor">
      <rect x="1" y="1" width="8" height="5" rx="1.5" />
      <rect x="11" y="1" width="8" height="5" rx="1.5" />
      <rect x="1" y="8" width="8" height="5" rx="1.5" />
      <rect x="11" y="8" width="8" height="5" rx="1.5" />
    </svg>
  ),
};

// â”€â”€â”€ Helpers â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
function readStored(key, validSet, fallback) {
  try {
    const v = window.localStorage.getItem(key);
    return validSet.has(v) ? v : fallback;
  } catch {
    return fallback;
  }
}

function normalizePair(entry) {
  const symbol = String(entry.pair_symbol || "")
    .replaceAll("_", "")
    .toUpperCase();
  const rawLabel = String(entry.pair_symbol || symbol);
  const label = rawLabel.includes("/")
    ? rawLabel.toUpperCase()
    : rawLabel.replace("_", "/").toUpperCase();
  return {
    symbol,
    label,
    icon: entry.icon || "",
    precision: entry.pricePrecision || 2,
    tickSize: entry.tickSize || 0.01,
    fallbackPrice:
      Number.isFinite(Number(entry.price)) && Number(entry.price) > 0
        ? Number(entry.price)
        : NaN,
    minStake: Number(entry.minStake) || 1,
    maxStake: Number(entry.maxStake) || 100,
    payoutMultiplier: Number(entry.payoutMultiplier) || 1.85,
  };
}

// function getDisplayPrice(pair) {
//   const price = Number(pair.price);

//   const integerDigits = Math.abs(Math.trunc(price)).toString().length;

//   const decimals = Math.max(0, 7 - integerDigits);

//   return price.toFixed(decimals);
// }



// â”€â”€â”€ LayoutPanel â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
function LayoutPanel({
  panelId,
  luckyData,
  getLuckyRoundDetails,
  chartFontSize = null,
  layoutVariant = false,
  activeLayout,
  onLayoutChange,
  selectedPair,
  onSelectPair,
  shouldShowLuckyModal = false,
  showLayoutMulti = false,
  onShowSpin,
}) {
  const prefix = `layout.${panelId}`;
  const layoutRef = useRef(null);

  const [rawPairs, setRawPairs] = useState([]);
  const [pairType, setPairType] = useState("crypto");
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const [timeframe, setTimeframe] = useState(() =>
    readStored(`${prefix}.timeframe`, TIMEFRAME_OPTIONS, "Tick"),
  );

  const [chartType, setChartType] = useState(() =>
    readStored(`${prefix}.chartType`, CHART_TYPE_OPTIONS, "candle"),
  );

  useEffect(() => {
    try {
      window.localStorage.setItem(`${prefix}.timeframe`, timeframe);
    } catch { }
  }, [timeframe, prefix]);

  useEffect(() => {
    try {
      window.localStorage.setItem(`${prefix}.chartType`, chartType);
    } catch { }
  }, [chartType, prefix]);

  // Fetch pairs helper
  const fetchPairs = (pageNum, type, isLoadMore = false) => {
    if (isLoadMore && isLoadingMore) return; // Prevent duplicate calls
    if (isLoadMore) {
      setIsLoadingMore(true);
    }

    Axios.post(`/get-pairs?page=${pageNum}&limit=50`, {
      type: type,
    })
      .then(({ data }) => {
        if (data?.success && Array.isArray(data.result?.docs)) {
          console.log("DATA", data);
          const formattedPairs = data.result.docs.map((pair) => {
            const symbole = pair.pair_symbol.replace("_", "/").toUpperCase();
            return {
              symbol: pair.original_symbol,
              label: symbole,
              ...pair,
            };
          });

          if (isLoadMore) {
            setRawPairs((prev) => [...prev, ...formattedPairs]);
          } else {
            setRawPairs(formattedPairs);
          }
          setHasMore(data.result?.hasNextPage || false);
        } else {
          if (!isLoadMore) setRawPairs([]);
          setHasMore(false);
        }
      })
      .catch((err) => {
        console.error("Layout pairs fetch error:", err);
        setHasMore(false);
      })
      .finally(() => {
        if (isLoadMore) {
          setIsLoadingMore(false);
        }
      });
  };

  // Initial fetch on type change
  useEffect(() => {
    setPage(1);
    fetchPairs(1, pairType, false);
  }, [pairType]);

  const handleLoadMorePairs = () => {
    if (!hasMore || isLoadingMore) return;
    const nextPage = page + 1;
    setPage(nextPage);
    fetchPairs(nextPage, pairType, true);
  };

  const topPairs = useMemo(() => rawPairs.map(normalizePair), [rawPairs]);

  const pairStats = useBinancePairsTicker(topPairs, "crypto");

  const activePairCfg = useMemo(
    () => topPairs.find((p) => p.symbol === selectedPair),
    [topPairs, selectedPair],
  );
  const activePair = useMemo(
    () => pairStats.find((p) => p.symbol === selectedPair),
    [pairStats, selectedPair],
  );

  const activePairLabel = useMemo(
    () => activePair?.label ?? activePairCfg?.label ?? "--",
    [activePair?.label, activePairCfg?.label],
  );

  const startPrice =
    (Number.isFinite(activePair?.price) ? activePair.price : null) ??
    activePairCfg?.fallbackPrice ??
    65948.64;

  return (
    <div
      ref={layoutRef}
      className="layout-panel-portal-root"
      style={{
        position: "relative",
        width: "100%",
        height: "100%",
        minWidth: 0,
        minHeight: 0,
        display: "flex",
        overflow: "hidden",
      }}
    >
      <UpDownChartBoard
        panelId={panelId}
        symbol={activePairLabel}
        precision={activePairCfg?.precision}
        tickSize={activePairCfg?.tickSize}
        luckyData={luckyData}
        getLuckyRoundDetails={getLuckyRoundDetails}
        marketSymbol={selectedPair}
        activePairStats={activePair}
        startingPrice={startPrice}
        timeframe={timeframe}
        onTimeframeChange={setTimeframe}
        chartType={chartType}
        onChartTypeChange={setChartType}
        pairs={rawPairs}
        pairType={pairType}
        setPairType={setPairType}
        selectedPair={selectedPair}
        onSelectPair={onSelectPair}
        hasMorePairs={hasMore}
        isLoadingMorePairs={isLoadingMore}
        onLoadMorePairs={handleLoadMorePairs}
        chartFontSize={chartFontSize}
        layoutVariant={layoutVariant}
        activeLayout={activeLayout}
        onLayoutChange={onLayoutChange}
        layoutRef={layoutRef}
        showLayoutMulti={showLayoutMulti}
        shouldShowLuckyModal={shouldShowLuckyModal}
        onShowSpin={onShowSpin}
      />
    </div>
  );
}

// â”€â”€â”€ LayoutPage â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
export function LayoutPage() {
  const [isMobileView, setIsMobileView] = useState(() => {
    if (
      typeof window === "undefined" ||
      typeof window.matchMedia !== "function"
    )
      return false;
    return window.matchMedia(MOBILE_LAYOUT_MEDIA_QUERY).matches;
  });

  const { wallet, setWallet } = useWallet();
  const [isLayoutLoading, setIsLayoutLoading] = useState(false);
  const [activeLayout, setActiveLayout] = useState(() => {
    try {
      const s = window.localStorage.getItem(LAYOUT_STORAGE_KEY);
      return LAYOUT_OPTIONS.some((l) => l.id === s) ? s : "single";
    } catch {
      return "single";
    }
  });

  const [allPairs, setAllPairs] = useState([]);
  const [luckyData, setLuckyData] = useState({});
  const hasAutoOpenedRef = useRef(false);

  const handleShowSpin = useCallback(() => {
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("open-lucky-wheel", { detail: { variant: "spin" } }));
    }
  }, []);
  const [selectedPairs, setSelectedPairs] = useState(() => {
    const pairs = [];
    for (let i = 0; i < 4; i++) {
      const stored = getStoredPair(i);
      pairs.push(stored || consts?.defaultCrypto);
    }
    return pairs;
  });

  function getStoredPair(index) {
    try {
      return window.localStorage.getItem(`chart_slot_${index}`) || null;
    } catch {
      return null;
    }
  }

  function savePair(index, pair) {
    try {
      window.localStorage.setItem(`chart_slot_${index}`, pair);
    } catch { }
  }

  useEffect(() => {
    if (
      typeof window === "undefined" ||
      typeof window.matchMedia !== "function"
    ) {
      return undefined;
    }

    const mediaQuery = window.matchMedia(MOBILE_LAYOUT_MEDIA_QUERY);
    const handleChange = (event) => {
      setIsMobileView(event.matches);
    };

    setIsMobileView(mediaQuery.matches);

    if (typeof mediaQuery.addEventListener === "function") {
      mediaQuery.addEventListener("change", handleChange);
      return () => mediaQuery.removeEventListener("change", handleChange);
    }
    mediaQuery.addListener(handleChange);
    return () => mediaQuery.removeListener(handleChange);
  }, []);

  const getWallet = useCallback(async () => {
    try {
      const { data } = await Axios.post("/users/getWallet", {});
      if (data?.success) {
        const hasSpinData = data.result?.spin_and_win?.hasSpin;
        setWallet({ ...data.result, hasSpin: hasSpinData });
      } else {
        setWallet({ balance: 0 });
      }
    } catch (error) {
      setWallet({ balance: 0 });
      console.error("Error fetching wallet:", error);
    }
  }, []);

  useEffect(() => {
    getWallet();
  }, [getWallet]);

  useEffect(() => {
    if (!isLayoutLoading) {
      return undefined;
    }

    const loadingTimer = setTimeout(() => {
      setIsLayoutLoading(false);
    }, 400);

    return () => {
      clearTimeout(loadingTimer);
    };
  }, [isLayoutLoading, activeLayout]);

  useEffect(() => {
    Axios.post("/get-pairs?page=1&limit=50", { type: "crypto" })
      .then(({ data }) => {
        if (data?.success && Array.isArray(data.result?.docs)) {
          const formatted = data.result.docs.map((pair) => {
            const symbole = pair.pair_symbol.replace("_", "/").toUpperCase();
            return {
              symbol: pair.original_symbol,
              label: symbole,
              ...pair,
            };
          });
          setAllPairs(formatted);
        }
      })
      .catch(() => setAllPairs([]));
  }, []);

  const getLuckyRoundDetails = useCallback(async () => {
    try {
      const { data } = await Axios.get("/users/getLuckyRoundDetails");

      if (
        data?.success &&
        data?.result?.isLucky &&
        data?.result?.multiplier > 1
      ) {
        setLuckyData(data?.result);
      } else {
        setLuckyData(null);
      }
    } catch (error) {
      console.error("Error fetching active trades:", error);
    }
  }, []);

  useEffect(() => {
    getLuckyRoundDetails();

    const handleRefresh = () => {
      getLuckyRoundDetails();
    };
    const handleClearLucky = () => {
      setLuckyData(null);
    };

    window.addEventListener("refresh-lucky-details", handleRefresh);
    window.addEventListener("clear-lucky-multiplier", handleClearLucky);
    return () => {
      window.removeEventListener("refresh-lucky-details", handleRefresh);
      window.removeEventListener("clear-lucky-multiplier", handleClearLucky);
    };
  }, [getLuckyRoundDetails]);

  useEffect(() => {
    if (allPairs.length === 0) return;
    setSelectedPairs((prev) => {
      const used = new Set();
      const newPairs = [...prev];
      for (let i = 0; i < 4; i++) {
        if (!newPairs[i] || used.has(newPairs[i])) {
          const available = allPairs.find((p) => !used.has(p.symbol));
          newPairs[i] = available ? available.symbol : consts?.defaultCrypto;
        }
        used.add(newPairs[i]);
      }
      // Save them
      for (let i = 0; i < 4; i++) {
        savePair(i, newPairs[i]);
      }
      return newPairs;
    });
  }, [allPairs]);

  const handleLayoutChange = (id) => {
    if (id === activeLayout) {
      return;
    }

    setIsLayoutLoading(true);
    setActiveLayout(id);
    try {
      window.localStorage.setItem(LAYOUT_STORAGE_KEY, id);
    } catch { }
  };

  const prevLayoutRef = useRef(activeLayout);
  useEffect(() => {
    const prev = prevLayoutRef.current;
    const prevConfig =
      LAYOUT_OPTIONS.find((l) => l.id === prev) || LAYOUT_OPTIONS[0];
    const newConfig = config;
    if (newConfig.panels > prevConfig.panels) {
      setSelectedPairs((prevPairs) => {
        const used = new Set(prevPairs.slice(0, prevConfig.panels));
        const newPairs = [...prevPairs];
        for (let i = prevConfig.panels; i < newConfig.panels; i++) {
          const available = allPairs.find((p) => !used.has(p.symbol));
          newPairs[i] = available ? available.symbol : consts?.defaultCrypto;
          used.add(newPairs[i]);
          savePair(i, newPairs[i]);
        }
        return newPairs;
      });
    }
    prevLayoutRef.current = activeLayout;
  }, [activeLayout, LAYOUT_OPTIONS.panels, allPairs]);

  const handleSelectPair = (slotIndex, newPair) => {
    setSelectedPairs((prev) => {
      const newPairs = [...prev];
      newPairs[slotIndex] = newPair;
      // Check conflict
      const conflictIndex = newPairs.findIndex(
        (p, i) => i !== slotIndex && p === newPair,
      );
      if (conflictIndex !== -1) {
        // Reassign conflict
        const used = new Set(newPairs.filter((_, i) => i !== conflictIndex));
        const available = allPairs.find((p) => !used.has(p.symbol));
        newPairs[conflictIndex] = available
          ? available.symbol
          : consts?.defaultCrypto;
        savePair(conflictIndex, newPairs[conflictIndex]);
      }
      savePair(slotIndex, newPair);
      return newPairs;
    });
  };

  const resolvedLayout = activeLayout;
  const config =
    LAYOUT_OPTIONS.find((l) => l.id === resolvedLayout) ?? LAYOUT_OPTIONS[0];
  const isSingle = activeLayout === "single";
  const isMulti = activeLayout !== "single";
  const isDesktop = !isMobileView;
  const useMultiDesktopLayout = isMulti && isDesktop;
  const isQuad = resolvedLayout === "quad";
  const isTriple = resolvedLayout === "triple"; // 2+1 layout
  const isDual = resolvedLayout === "dual";
  const multiPanelChartFontSize = isDual ? 12 : 11;
  const mobilePanelGridStyle = isMobileView
    ? {
      gridTemplateColumns: "minmax(0, 1fr)",
      gridTemplateRows: `repeat(${config.panels}, minmax(0, auto))`,
    }
    : {};

  // Shared cell wrapper style
  const cellStyle = (extraStyle = {}) => ({
    width: "100%",
    minWidth: 0,
    minHeight: 0,
    display: "flex",
    overflow: "hidden",
    ...extraStyle,
  });

  return (
    <EZ5050TradingLayout
        wallet={wallet}
        activePairLabel={selectedPairs[0] || "BTC/USDT"}
        payoutRate={0.82}
        luckyData={luckyData}
        isReady={!isLayoutLoading}
      >
    <div
      style={{
        width: "100%",
        padding: "8px 10px",
        display: "flex",
        flexDirection: "column",
        gap: "8px",
        boxSizing: "border-box",
        background: "#0b0e14",
        ...(useMultiDesktopLayout && {
          height: `calc(100vh - ${HEADER_H}px)`,
          overflow: "hidden",
        }),
        ...(!isDesktop && {
          height: "100%",
          overflow: "hidden",
        }),
        ...(isSingle && {
          height: "100%",
          overflow: "hidden",
        }),
        flex: 1,
        minHeight: 0,
      }}
      className={`layout-page${isSingle ? " layout-page--single" : ""}${isMulti ? " layout-page--multi" : ""}`}
    >
      <Loader isLoading={isLayoutLoading} />
      {/* â”€â”€ Layout switcher â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
      {/* {isMobileView ? ( */}
      {/* <div style={S.bar}>
          <span style={S.barLabel}>Layout</span>
          <div style={{ display: 'flex', gap: '4px',flexWrap: 'wrap' }}>
            {LAYOUT_OPTIONS.map((opt) => {
              const active = activeLayout === opt.id;
              return (
                <button
                  key={opt.id}
                  onClick={() => handleLayoutChange(opt.id)}
                  title={opt.label}
                  style={{
                    ...S.btn,
                    background: active ? 'rgba(37,99,235,0.18)' : 'rgba(255,255,255,0.04)',
                    border:     active ? '1.5px solid #3b82f6'  : '1.5px solid rgba(255,255,255,0.08)',
                    color:      active ? '#60a5fa'               : '#6b7280',
                  }}
                >
                  {ICON[opt.id]}
                  <span style={{ fontSize: '11px', fontWeight: 600 }}>{opt.label}</span>
                </button>
              );
            })}
          </div>
        </div> */}
      {/* â”€â”€ Panel grid â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}

      {isTriple ? (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: isMobileView ? "minmax(0, 1fr)" : "1fr 1fr",
            gridTemplateRows: isMobileView
              ? "repeat(3, minmax(0, auto))"
              : "1fr 1fr",
            gap: `${GRID_GAP}px`,
            ...(useMultiDesktopLayout && {
              flex: 1,
              minHeight: 0,
              overflow: "hidden",
            }),
          }}
        >
          {/* Top-left */}
          <div style={cellStyle()}>
            <LayoutPanel
              panelId="panel-1"
              luckyData={luckyData}
              getLuckyRoundDetails={getLuckyRoundDetails}
              chartFontSize={multiPanelChartFontSize}
              layoutVariant
              activeLayout={activeLayout}
              onLayoutChange={handleLayoutChange}
              selectedPair={selectedPairs[0]}
              onSelectPair={(pair) => handleSelectPair(0, pair)}
              showLayoutMulti={false}
              shouldShowLuckyModal={true}
              onShowSpin={handleShowSpin}
            />
          </div>

          {/* Top-right */}
          <div style={cellStyle()}>
            <LayoutPanel
              panelId="panel-2"
              luckyData={luckyData}
              getLuckyRoundDetails={getLuckyRoundDetails}
              chartFontSize={multiPanelChartFontSize}
              layoutVariant
              activeLayout={activeLayout}
              onLayoutChange={handleLayoutChange}
              selectedPair={selectedPairs[1]}
              onSelectPair={(pair) => handleSelectPair(1, pair)}
              showLayoutMulti={false}
              shouldShowLuckyModal={false}
              onShowSpin={handleShowSpin}
            />
          </div>

          {/* Bottom â€” spans full width */}
          <div style={cellStyle({ gridColumn: "1 / -1" })}>
            <LayoutPanel
              panelId="panel-3"
              luckyData={luckyData}
              getLuckyRoundDetails={getLuckyRoundDetails}
              chartFontSize={multiPanelChartFontSize}
              layoutVariant
              activeLayout={activeLayout}
              onLayoutChange={handleLayoutChange}
              selectedPair={selectedPairs[2]}
              onSelectPair={(pair) => handleSelectPair(2, pair)}
              showLayoutMulti={false}
              shouldShowLuckyModal={false}
              onShowSpin={handleShowSpin}
            />
          </div>
        </div>
      ) : (
        /* Single / Dual / Quad */
        <div
          style={{
            display: "grid",
            gap: `${GRID_GAP}px`,
            gridTemplateColumns: `repeat(${config.cols}, minmax(0, 1fr))`,
            ...(isQuad &&
              useMultiDesktopLayout && {
              gridTemplateRows: "1fr 1fr",
              flex: 1,
              minHeight: 0,
              overflow: "hidden",
            }),
            ...(isDual &&
              useMultiDesktopLayout && {
              gridTemplateRows: "1fr 1fr",
              flex: 1,
              minHeight: 0,
              overflow: "hidden",
            }),
            ...mobilePanelGridStyle,
            ...(isSingle && {
              gridTemplateRows: "minmax(0, 1fr)",
              flex: 1,
              minHeight: 0,
              overflow: "hidden",
            }),
          }}
        >
          {Array.from({ length: config.panels }, (_, i) => (
            <div
              key={i}
              style={cellStyle(
                isSingle ? { height: "100%" } : { height: "100%" },
              )}
            >
              {isQuad || isDual ? (
                <LayoutPanel
                  panelId={`panel-${i + 1}`}
                  luckyData={luckyData}
                  getLuckyRoundDetails={getLuckyRoundDetails}
                  chartFontSize={multiPanelChartFontSize}
                  layoutVariant
                  activeLayout={activeLayout}
                  onLayoutChange={handleLayoutChange}
                  selectedPair={selectedPairs[i]}
                  onSelectPair={(pair) => handleSelectPair(i, pair)}
                  shouldShowLuckyModal={i === 0}
                  showLayoutMulti={false}
                  onShowSpin={handleShowSpin}
                />
              ) : (
                <LayoutPanel
                  panelId={`panel-${i + 1}`}
                  luckyData={luckyData}
                  getLuckyRoundDetails={getLuckyRoundDetails}
                  activeLayout={activeLayout}
                  onLayoutChange={handleLayoutChange}
                  selectedPair={selectedPairs[i]}
                  onSelectPair={(pair) => handleSelectPair(i, pair)}
                  shouldShowLuckyModal={i === 0}
                  showLayoutMulti={false}
                  onShowSpin={handleShowSpin}
                />
              )}
            </div>
          ))}
        </div>
      )}
    </div>
    </EZ5050TradingLayout>
  );
}

// â”€â”€â”€ Static styles â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
const S = {
  bar: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "5px 10px",
    background: "rgba(255,255,255,0.03)",
    border: "1px solid rgba(255,255,255,0.07)",
    borderRadius: "8px",
    alignSelf: "flex-start",
    flexShrink: 0,
    flexWrap: "wrap",
  },
  barLabel: {
    fontSize: "10px",
    color: "#6b7280",
    fontWeight: 700,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    marginRight: "4px",
  },
  btn: {
    display: "flex",
    alignItems: "center",
    gap: "5px",
    padding: "4px 10px",
    borderRadius: "6px",
    cursor: "pointer",
    transition: "background 0.15s, border-color 0.15s, color 0.15s",
    userSelect: "none",
  },
};
